import React, { useCallback, useEffect, useState } from "react";
import { Pressable, StyleSheet, TextInput, View } from "react-native";
import {
  ArrowSquareOutIcon,
  ChatTeardropIcon,
  CheckIcon,
  ClipboardTextIcon,
  FileTextIcon,
  LinkIcon,
  ListChecksIcon,
  PencilSimpleIcon,
  ShieldIcon,
  SparkleIcon,
  XIcon,
} from "@phosphor-icons/react";

import CustomText from "../../../../../components/common/general/CustomText/CustomText";
import { colors } from "../../../../../styles/design-system";
import type { CourseEvaluatorReviewSidebarItem } from "../types/reviewTypes";
import type { ActivityDetail, StructuredFields } from "../types/reviewTypes";
import { AIRewriteActionButton, AIRewriteModal, useAIRewriteSuggestion } from "./AIRewriteControls";
import ActivityPanelTabs from "./ActivityPanelTabs";
import RecommendationSourceList from "./RecommendationSourceList";

interface SuggestedContentPanelProps {
  selectedReviewItem: CourseEvaluatorReviewSidebarItem;
  activityDetail: ActivityDetail | null;
  isEditDisabled?: boolean;
  onSaveEdit?: (editedFields: StructuredFields) => void;
}

// ---------------------------------------------------------------------------
// Suggested content type presentation
// ---------------------------------------------------------------------------

function getSuggestedTypePresentation(suggestedType: string | undefined, activityType: string | undefined) {
  const type = suggestedType || activityType;
  switch (type) {
    case "ile":
      return { label: "Interactive Learning Experience", icon: SparkleIcon, bgColor: "#F3EEFF", textColor: "#7C3AED" };
    case "quiz":
      return { label: "Quiz", icon: ClipboardTextIcon, bgColor: "#EFF6FF", textColor: "#2563EB" };
    case "discussion":
      return { label: "Discussion", icon: ChatTeardropIcon, bgColor: "#F0FDF4", textColor: "#16A34A" };
    case "resource":
    case "externalLink":
      return { label: "External Link", icon: LinkIcon, bgColor: "#EFF6FF", textColor: "#2563EB" };
    case "html-content":
      return { label: "HTML Content", icon: FileTextIcon, bgColor: "#FEF3C7", textColor: "#D97706" };
    case "checklist":
      return { label: "Checklist", icon: ListChecksIcon, bgColor: "#F0FDF4", textColor: "#16A34A" };
    default:
      return { label: "Resource", icon: FileTextIcon, bgColor: colors.grey[100], textColor: colors.grey[700] };
  }
}

/**
 * Returns true for content types that should show a Proposed-only editor
 * card (single-pane) instead of a Description+URL card.
 */
function isEditableContentType(activityType: string | undefined): boolean {
  return activityType === "checklist" || activityType === "html-content";
}

// ---------------------------------------------------------------------------
// Proposed-only editor card (for checklist / html-content)
// ---------------------------------------------------------------------------

interface ProposedEditorCardProps {
  title: string;
  content: string;
  isEditDisabled?: boolean;
  onSave?: (editedContent: string) => void;
}

function ProposedEditorCard({ title, content, isEditDisabled, onSave }: ProposedEditorCardProps) {
  const canEdit = Boolean(onSave) && !isEditDisabled;
  const [isEditing, setIsEditing] = useState(false);
  const [editingContent, setEditingContent] = useState(content);
  const getRewriteProposedText = useCallback(() => editingContent, [editingContent]);
  const {
    closeRewriteModal,
    isRewriteModalVisible,
    isRewriting,
    openRewriteModal,
    rewriteErrorMessage,
    submitRewrite,
  } = useAIRewriteSuggestion({
    getProposedText: getRewriteProposedText,
    onApplyRewrite: setEditingContent,
    title,
  });

  useEffect(() => {
    setIsEditing(false);
    setEditingContent(content);
  }, [content]);

  useEffect(() => {
    if (!canEdit) {
      setIsEditing(false);
    }
  }, [canEdit]);

  const handleEdit = useCallback(() => {
    if (!canEdit) return;
    setEditingContent(content);
    setIsEditing(true);
  }, [canEdit, content]);

  const handleSave = useCallback(() => {
    if (!canEdit) return;
    setIsEditing(false);
    onSave?.(editingContent);
  }, [canEdit, editingContent, onSave]);

  const handleCancel = useCallback(() => {
    setIsEditing(false);
    setEditingContent(content);
  }, [content]);

  return (
    <View style={proposedStyles.card}>
      {/* Green header */}
      <View style={proposedStyles.header}>
        <CustomText
          text="Proposed"
          useTranslationText={false}
          size="s"
          weight="ultraBold"
          style={proposedStyles.headerText}
        />
        {isEditing ? (
          <View style={proposedStyles.editingActions}>
            <Pressable onPress={handleCancel} style={proposedStyles.cancelButton}>
              <XIcon size={12} color={colors.grey[600]} weight="bold" />
              <CustomText text="Cancel" useTranslationText={false} size="xs" style={proposedStyles.cancelButtonText} />
            </Pressable>
            <AIRewriteActionButton disabled={isRewriting} onPress={openRewriteModal} />
            <Pressable onPress={handleSave} style={proposedStyles.saveButton}>
              <CheckIcon size={12} color={colors.white} weight="bold" />
              <CustomText text="Save" useTranslationText={false} size="xs" style={proposedStyles.saveButtonText} />
            </Pressable>
          </View>
        ) : (
          <Pressable
            accessibilityRole="button"
            accessibilityLabel="Edit proposed changes"
            accessibilityState={{ disabled: !canEdit }}
            disabled={!canEdit}
            onPress={handleEdit}
            style={[proposedStyles.editButton, !canEdit && proposedStyles.editButtonDisabled]}>
            <PencilSimpleIcon size={14} color={canEdit ? colors.grey[700] : colors.grey[400]} weight="regular" />
            <CustomText
              text="Edit"
              useTranslationText={false}
              size="xs"
              style={[proposedStyles.editButtonText, !canEdit && proposedStyles.editButtonTextDisabled]}
            />
          </Pressable>
        )}
      </View>

      {/* Content body */}
      <View style={proposedStyles.body}>
        {isEditing ? (
          <TextInput
            value={editingContent}
            onChangeText={setEditingContent}
            multiline
            style={proposedStyles.textInput}
            textAlignVertical="top"
          />
        ) : (
          <>
            {title ? (
              <CustomText
                text={title}
                useTranslationText={false}
                size="s"
                weight="bold"
                style={proposedStyles.contentTitle}
              />
            ) : null}
            <CustomText text={content} useTranslationText={false} size="s" style={proposedStyles.contentText} />
          </>
        )}
      </View>
      <AIRewriteModal
        errorMessage={rewriteErrorMessage}
        isRewriting={isRewriting}
        onCancel={closeRewriteModal}
        onRewrite={submitRewrite}
        visible={isRewriteModalVisible}
      />
    </View>
  );
}

const proposedStyles = StyleSheet.create({
  card: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  header: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 16,
    height: 40,
    backgroundColor: "#F3FEE7",
    borderBottomWidth: 1,
    borderBottomColor: colors.grey[200],
  },
  headerText: {
    color: colors.success[700],
  },
  editButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: colors.grey[200],
    backgroundColor: colors.white,
  },
  editButtonText: {
    color: colors.grey[700],
  },
  editButtonDisabled: {
    backgroundColor: colors.grey[50],
    borderColor: colors.grey[200],
    opacity: 0.7,
  },
  editButtonTextDisabled: {
    color: colors.grey[400],
  },
  editingActions: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  cancelButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 6,
  },
  cancelButtonText: {
    color: colors.grey[600],
  },
  saveButton: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    paddingHorizontal: 10,
    paddingVertical: 4,
    borderRadius: 6,
    backgroundColor: colors.brand[600],
  },
  saveButtonText: {
    color: colors.white,
  },
  body: {
    padding: 20,
  },
  contentTitle: {
    color: colors.grey[900],
    marginBottom: 8,
  },
  contentText: {
    color: colors.grey[700],
    lineHeight: 22,
  },
  textInput: {
    minHeight: 200,
    padding: 12,
    fontSize: 14,
    lineHeight: 22,
    color: colors.grey[900],
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[300],
    borderRadius: 8,
    fontFamily: "Inter",
  },
});

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

function SuggestedContentPanel({
  selectedReviewItem,
  activityDetail,
  isEditDisabled,
  onSaveEdit,
}: SuggestedContentPanelProps) {
  const detail = activityDetail;
  const typePresentation = getSuggestedTypePresentation(detail?.suggestedContentType, detail?.activityType);
  const TypeIcon = typePresentation.icon;
  const hasUrl = !!detail?.url;
  const showProposedEditor = isEditableContentType(detail?.activityType);
  const isIleType = (detail?.suggestedContentType || detail?.activityType) === "ile";
  const hasRecommendationSources = Boolean(detail?.recommendationSources?.length);
  const shouldShowFeedback = Boolean(detail?.feedback && !hasRecommendationSources);
  const shouldShowLearningObjective = Boolean(detail?.learningObjective && !hasRecommendationSources);

  return (
    <View style={styles.panel}>
      {/* Badge row: New + Type badge + "Recommended new activity" label */}
      <View style={styles.badgesRow}>
        <View style={styles.newBadge}>
          <CustomText text="New" useTranslationText={false} size="xs" weight="medium" style={styles.newBadgeText} />
        </View>
        <View style={[styles.typeBadge, { backgroundColor: typePresentation.bgColor }]}>
          <TypeIcon size={14} color={typePresentation.textColor} weight="bold" />
          <CustomText
            text={typePresentation.label}
            useTranslationText={false}
            size="xs"
            weight="medium"
            style={{ color: typePresentation.textColor }}
          />
        </View>
        <CustomText
          text="Recommended new activity"
          useTranslationText={false}
          size="xs"
          style={styles.recommendedLabel}
        />
      </View>

      {/* Title — with ILE subtitle when applicable */}
      {isIleType ? (
        <View style={styles.titleRow}>
          <CustomText
            text={detail?.recommendedTitle ?? selectedReviewItem.title}
            useTranslationText={false}
            size="xl"
            weight="bold"
            style={styles.title}
          />
          <CustomText
            text=" — Axio Interactive Learning Experience"
            useTranslationText={false}
            size="xl"
            style={styles.ileSubtitle}
          />
        </View>
      ) : (
        <CustomText
          text={detail?.recommendedTitle ?? selectedReviewItem.title}
          useTranslationText={false}
          size="xl"
          weight="bold"
          style={styles.title}
        />
      )}

      <ActivityPanelTabs attachments={detail?.attachments}>
        <View style={styles.reviewContent}>
          {/* Why card — with dividers between sections */}
          {(shouldShowFeedback || shouldShowLearningObjective || hasRecommendationSources) && (
            <View style={styles.whyCard}>
              <CustomText
                text="Why we're suggesting this"
                useTranslationText={false}
                size="s"
                weight="bold"
                style={styles.whyHeading}
              />
              {shouldShowFeedback && (
                <CustomText text={detail.feedback} useTranslationText={false} size="xs" style={styles.whyBody} />
              )}
              <RecommendationSourceList recommendations={detail?.recommendationSources} />

              {/* Divider before learning objective */}
              {shouldShowLearningObjective && (
                <>
                  <View style={styles.whyDivider} />
                  <View style={styles.learningObjectiveRow}>
                    <CustomText
                      text="Learning objective: "
                      useTranslationText={false}
                      size="xs"
                      weight="medium"
                      style={styles.learningObjectiveLabel}
                    />
                    <CustomText
                      text={detail?.learningObjective ?? ""}
                      useTranslationText={false}
                      size="xs"
                      style={styles.learningObjectiveLabel}
                    />
                  </View>
                </>
              )}

              {/* Divider before rubric standard */}
              {detail?.rubricCode && (
                <>
                  <View style={styles.whyDivider} />
                  <View style={styles.rubricRow}>
                    <ShieldIcon size={12} color={colors.brand[600]} weight="regular" />
                    <CustomText
                      text={`${detail.rubricCode}: ${detail.rubricTitle}`}
                      useTranslationText={false}
                      size="xs"
                      weight="medium"
                      style={styles.rubricLink}
                    />
                    {detail.rubricDescription && (
                      <CustomText
                        text={` \u2014 ${detail.rubricDescription}`}
                        useTranslationText={false}
                        size="xs"
                        style={styles.rubricDescription}
                      />
                    )}
                  </View>
                </>
              )}
            </View>
          )}

          {/* Proposed-only editor card for checklist / html-content types */}
          {showProposedEditor && detail?.recommendedDescription && (
            <ProposedEditorCard
              title={detail.recommendedTitle ?? ""}
              content={detail.recommendedDescription}
              isEditDisabled={isEditDisabled}
              onSave={(description) =>
                onSaveEdit?.({
                  title: detail.recommendedTitle ?? selectedReviewItem.title,
                  description,
                })
              }
            />
          )}

          {/* Description + URL card for external link / resource types */}
          {!showProposedEditor && (detail?.recommendedDescription || hasUrl) && (
            <View style={styles.contentCard}>
              {detail?.recommendedDescription && (
                <View style={styles.contentSection}>
                  <CustomText
                    text="Description"
                    useTranslationText={false}
                    size="s"
                    weight="ultraBold"
                    style={styles.contentHeading}
                  />
                  <CustomText
                    text={detail.recommendedDescription}
                    useTranslationText={false}
                    size="s"
                    style={styles.contentBody}
                  />
                </View>
              )}

              {hasUrl && (
                <View style={styles.urlSection}>
                  <CustomText text="URL" useTranslationText={false} size="xs" weight="bold" style={styles.urlLabel} />
                  <View style={styles.urlRow}>
                    <ArrowSquareOutIcon size={14} color={colors.brand[600]} weight="regular" />
                    <CustomText
                      text={detail?.url ?? ""}
                      useTranslationText={false}
                      size="xs"
                      weight="medium"
                      style={styles.urlText}
                      numberOfLines={1}
                    />
                  </View>
                </View>
              )}
            </View>
          )}

          {/* Create ILE button for ILE type */}
          {isIleType && (
            <Pressable style={styles.createIleButton}>
              <SparkleIcon size={14} color={colors.white} weight="fill" />
              <CustomText
                text="Create ILE"
                useTranslationText={false}
                size="xs"
                weight="medium"
                style={styles.createIleButtonText}
              />
            </Pressable>
          )}

          {/* Last modified */}
          {detail?.lastModifiedBy && (
            <CustomText
              text={`Last modified by ${detail.lastModifiedBy} on ${detail.lastModifiedAt ?? "unknown date"}`}
              useTranslationText={false}
              size="xs"
              style={styles.lastModified}
            />
          )}
        </View>
      </ActivityPanelTabs>
    </View>
  );
}

const styles = StyleSheet.create({
  panel: {
    gap: 16,
  },
  reviewContent: {
    gap: 16,
  },
  badgesRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 8,
  },
  newBadge: {
    paddingHorizontal: 8,
    height: 24,
    justifyContent: "center",
    borderRadius: 8,
    backgroundColor: "#E7F7F8",
  },
  newBadgeText: {
    color: "#15727D",
  },
  typeBadge: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
    paddingHorizontal: 8,
    height: 24,
    borderRadius: 8,
  },
  recommendedLabel: {
    color: colors.grey[500],
  },
  titleRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    alignItems: "baseline",
  },
  title: {
    color: colors.grey[900],
  },
  ileSubtitle: {
    color: colors.grey[500],
  },
  whyCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    padding: 16,
    gap: 8,
  },
  whyHeading: {
    color: colors.grey[900],
    marginBottom: 2,
  },
  whyBody: {
    color: colors.grey[700],
    lineHeight: 20,
  },
  whyDivider: {
    height: 1,
    backgroundColor: colors.grey[200],
    marginVertical: 4,
  },
  learningObjectiveRow: {
    flexDirection: "row",
    flexWrap: "wrap",
  },
  learningObjectiveLabel: {
    color: colors.grey[500],
  },
  rubricRow: {
    flexDirection: "row",
    alignItems: "center",
    flexWrap: "wrap",
    gap: 4,
  },
  rubricLink: {
    color: colors.brand[600],
  },
  rubricDescription: {
    color: colors.grey[500],
  },
  contentCard: {
    backgroundColor: colors.white,
    borderWidth: 1,
    borderColor: colors.grey[200],
    borderRadius: 16,
    overflow: "hidden",
  },
  contentSection: {
    padding: 16,
    gap: 8,
  },
  contentHeading: {
    color: colors.grey[900],
  },
  contentBody: {
    color: colors.grey[700],
    lineHeight: 22,
  },
  urlSection: {
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: colors.grey[200],
    gap: 6,
  },
  urlLabel: {
    color: colors.grey[500],
    fontSize: 10,
    letterSpacing: 0.5,
  },
  urlRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 6,
  },
  urlText: {
    color: colors.brand[600],
    flex: 1,
  },
  // --- Create ILE button ---
  createIleButton: {
    flexDirection: "row",
    alignItems: "center",
    alignSelf: "flex-start",
    gap: 6,
    paddingHorizontal: 10,
    paddingVertical: 4,
    height: 26,
    borderRadius: 6,
    backgroundColor: colors.brand[600],
  },
  createIleButtonText: {
    color: colors.white,
  },
  lastModified: {
    color: colors.grey[400],
  },
});

export default SuggestedContentPanel;
